import * as yup from "yup";
import { CareerCenterEducationTypes } from "../../domain";
import { CareerCenterJobCategories } from "../../domain/CareerCenterJobCategories";
import { ReactSelectOption } from "../../models/generic";
import { FormValidationModel } from "../../models/validation";
import { AddressMinData, AddressMinSchema, DefaultUSCountry, ReactSelectOptionSchema } from "../generic";
import { MediaUploadData, MediaUploadRequiredSchema } from "../upload/MediaUpload";

export interface PostResumeData {
    categoryId: number;
    cv?: MediaUploadData;
    coverLetter: string;
    education: PostResumeEducationData[];
    experience: PostResumeExperienceData[];
    relocationPref: PostResumeRelocationData[];
    subscribe: boolean;
    title: string;
}

export class PostResume implements FormValidationModel<PostResumeData> {
    public data: PostResumeData;
    public rules: yup.Schema<PostResumeData>;

    public constructor() {
        this.data = {
            categoryId: CareerCenterJobCategories.RetinaSpecialist.id,
            coverLetter: "",
            education: [DefaultPostResumeObjects.Education()],
            experience: [DefaultPostResumeObjects.Experience()],
            relocationPref: [{
                id: 0,
                index: 0,
                isDelete: false,
                location: {
                    countryCode: DefaultUSCountry,
                },
            }],
            subscribe: true,
            title: "",
        };
        this.rules = yup.object<PostResumeData>().shape({
            categoryId: yup.number().required().label("Job category"),
            coverLetter: yup.string().required().label("Body"),
            cv: MediaUploadRequiredSchema.label("CV/Resume"),
            education: yup.array().of(yup.object<PostResumeEducationData>().shape({
                completedOn: yup.date().label("Completion date"),
                isDelete: yup.bool(),
                id: yup.number().required().min(0).label("Education ID"),
                index: yup.number(),
                organization: yup.string().when(["isDelete"], (isDelete: boolean) => {
                    if (!isDelete)
                        return yup.string().max(255).label("Institution name");
                    return yup.string();
                }),
                typeId: yup.number().when(["isDelete"], (isDelete: boolean) => {
                    if (!isDelete)
                        return yup.number().oneOf([0, ...CareerCenterEducationTypes.Values.map((v) => v.id)], "Invalid education type").label("Education");
                    return yup.number();
                }),
            })),
            experience: yup.array().of(yup.object<PostResumeExperienceData>().shape({
                isDelete: yup.bool(),
                id: yup.number().required().min(0).label("Experience ID"),
                index: yup.number(),
                organization: yup.string().when(["isDelete"], (isDelete: boolean) => {
                    if (!isDelete)
                        return yup.string().max(255).label("Company/institution");
                    return yup.string();
                }),
                position: yup.string().when(["isDelete"], (isDelete: boolean) => {
                    if (!isDelete)
                        return yup.string().max(255).label("Position");
                    return yup.string();
                }),
            })),
            relocationPref: yup.array().of(yup.object<PostResumeRelocationData>().shape({
                isDelete: yup.bool(),
                id: yup.number().required().min(0).label("Relocation ID"),
                index: yup.number(),
                location: AddressMinSchema.when(["isDelete"], (isDelete: boolean) => {
                    if (!isDelete)
                        return AddressMinSchema;
                    return yup.object();
                }),
                region: ReactSelectOptionSchema,
            })),
            subscribe: yup.bool().label("Subscribe to job alerts"),
            title: yup.string().required().label("Position title").max(255),
        });
    }
}

export interface PostResumeEducationData {
    id: number;
    index: number;
    isDelete: boolean;
    organization: string;
    completedOn?: Date;
    typeId: number;
}

export interface PostResumeExperienceData {
    id: number;
    index: number;
    isDelete: boolean;
    organization: string;
    position: string;
}

export interface PostResumeRelocationData {
    id: number;
    index: number;
    isDelete: boolean;
    location: AddressMinData;
    region?: ReactSelectOption | { region: string } | null;
}

export class DefaultPostResumeObjects {
    public static Education() {
        const x: PostResumeEducationData = {
            id: 0,
            index: 0,
            isDelete: false,
            organization: "",
            typeId: 0,
        };
        return x;
    }

    public static Experience() {
        const x: PostResumeExperienceData = {
            id: 0,
            index: 0,
            isDelete: false,
            organization: "",
            position: "",
        };
        return x;
    }

    public static RelocationPref() {
        const x: PostResumeRelocationData = {
            id: 0,
            index: 0,
            isDelete: false,
            location: { countryCode: DefaultUSCountry },
        };
        return x;
    }
}



// WEBPACK FOOTER //
// ./src/forms/careerCenter/PostResume.ts